import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Text,
  Paper,
  Group,
  Button,
  Divider,
  Checkbox,
  Anchor,
  Stack,
  Container,
  Alert,
  Badge,
  NumberFormatter,
} from "@mantine/core";
import { IconGift } from "@tabler/icons-react";
import { GoogleButton } from "../_Shared/GoogleButton";
import { userRegister } from "./authApi";
import { useStore } from "../store";
import { Link, useLocation, useSearch } from "wouter";
import { socialLoginFactory } from "./socialLogin";
import { useTranslation } from "react-i18next";
import { jsonGet } from "../api";

export default function RegisterForm({ params, realm, touLink, hideGoogle, ...props }) {
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [signupCodeInfo, setSignupCodeInfo] = useState(null);
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const signupCode = searchParams.get("code") || "";

  const form = useForm({
    initialValues: {
      realm: realm,
      email: "",
      name: "",
      terms: false,
      invitationToken: "",
      signupCode: signupCode,
    },
    validate: {
      email: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)
          ? null
          : "Invalid email",
    },
  });

  const { t } = useTranslation("Auth");

  const authToken = useStore((state) => state.authToken);
  const setAuthToken = useStore((state) => state.setAuthToken);
  const [, setLocation] = useLocation();

  const socialLogin = socialLoginFactory((event) => {
    setAuthToken(event.data.token);
    setLocation("/");
  });

  useEffect(() => {
    if (authToken) setLocation("/");
  }, [authToken]);

  // Validate signup code on mount
  useEffect(() => {
    if (signupCode) {
      jsonGet(`signup-codes/${encodeURIComponent(signupCode)}/validity`)
        .then((data) => {
          if (data?.valid) {
            setSignupCodeInfo(data);
          }
        })
        .catch(() => {});
    }
  }, [signupCode]);

  if (registerSuccess) {
    return (
      <Container size="xs" mt="xl">
        <Paper radius="md" p="xl" withBorder {...props}>
          <Text size="lg" fw={500}>
            Thank you!
          </Text>
          <Text mt="lg">
            Please check your email and follow the link to activate your
            account.
          </Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" mt="xl">
      <Paper radius="md" p="xl" withBorder {...props}>
        <Text size="lg" fw={500}>
          Register
        </Text>

        {signupCodeInfo && (
          <Alert
            color="teal"
            mt="md"
            icon={<IconGift size={18} />}
            title="Bonus credits included"
          >
            {signupCodeInfo.description || "Your signup code includes"}{" "}
            <Badge color="teal" variant="light" size="lg">
              <NumberFormatter
                value={signupCodeInfo.creditsToGrant / 100}
                thousandSeparator
              />{" "}
              free SKUs
            </Badge>
          </Alert>
        )}

        {!hideGoogle && (
        <Group grow mb="md" mt="md">
          <GoogleButton
            onClick={() =>
              socialLogin("Google", params?.token, signupCode || null)
            }
            radius="xl"
          >
            Google
          </GoogleButton>
        </Group>
        )}

        {!hideGoogle && (
        <Divider
          label="Or continue with email"
          labelPosition="center"
          my="lg"
        />
        )}

        <form
          onSubmit={form.onSubmit((values) => {
            console.debug("sending: ", values);
            userRegister(values).then((data) => {
              setRegisterSuccess(true);
            });
          })}
        >
          <Stack>
            <TextInput
              required
              label={t("name")}
              placeholder={t("name_placeholder")}
              value={form.values.name}
              onChange={(event) =>
                form.setFieldValue("name", event.currentTarget.value)
              }
              radius="md"
            />

            <TextInput
              required
              label={t("email")}
              placeholder={t("email_placeholder")}
              value={form.values.email}
              onChange={(event) =>
                form.setFieldValue("email", event.currentTarget.value)
              }
              error={form.errors.email && "Invalid email"}
              radius="md"
            />

            <Checkbox
              required
              label={
                <>
                  I accept the{" "}
                  <Anchor target="_blank" href={touLink} inherit>
                    terms and conditions
                  </Anchor>
                </>
              }
              checked={form.values.terms}
              onChange={(event) =>
                form.setFieldValue("terms", event.currentTarget.checked)
              }
            />
          </Stack>

          <Group justify="space-between" mt="xl">
            <Anchor href="/auth/login" component={Link} c="dimmed" size="xs">
              Already have an account? Login!
            </Anchor>
            <Button type="submit" radius="xl">
              {t("register")}
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
